<template>
	<div
		v-if="shouldBeDisplayed"
		ref="root"
		class="animation-wrapper"
		:class="wrapperClass"
		:style="wrapperStyle"
	>
		<div class="top-bar">
			<div class="top-bar-content">
				{{ eventName }}
			</div>
			<div class="top-bar-content clone">
				{{ eventName }}
			</div>
		</div>
		<div class="center-line">
			<div class="player-section-image">
				<div class="player-section-image-content">
					<img
						:src="playerImage"
						class="player-image"
					>
				</div>
				<div class="player-section-image-content clone">
					<img
						:src="playerImage"
						class="player-image"
					>
				</div>
			</div>
			<div class="player-section-info">
				<div class="player-section-info-content">
					<span class="player-number">{{ playerNumber }}</span>
					<span class="player-name">{{ playerName }}</span>
				</div>
				<div class="player-section-info-content clone">
					<span class="player-number">{{ playerNumber }}</span>
					<span class="player-name">{{ playerName }}</span>
				</div>
			</div>
			<div
				v-if="teamLogo"
				class="team-section"
			>
				<div class="blade-container">
					<div class="blade blade-a" />
					<div class="blade blade-b" />
				</div>
				<div class="team-logo">
					<div class="team-logo-content">
						<img :src="teamLogo">
					</div>
					<div class="team-logo-content clone">
						<img :src="teamLogo">
					</div>
				</div>
			</div>
		</div>
		<div class="center-underline">
			<div class="center-underline clone" />
		</div>
	</div>
</template>

<script>
export default {
	name:  'GameEventOverlay',
	props: {
		type: {
			type:    String,
			default: 'default',
		},
		eventName: {
			type:    String,
			default: '',
		},
		playerName: {
			type:    String,
			default: '',
		},
		playerNumber: {
			type:    String,
			default: '',
		},
		playerImage: {
			type:    String,
			default: '',
		},
		teamLogo: {
			type:    String,
			default: '',
		},
		playerWidth: {
			type:     Number,
			required: true,
		},
	},
	data() {
		return {
			isAnimated: false,
		};
	},
	computed: {
		wrapperClass() {
			return {
				[`type-${this.type}`]: true,
				animated:              this.isAnimated,
			};
		},
		wrapperStyle() {
			const scale = (this.playerWidth ?? 1920) / 1920;

			return {
				transform: `scale(${scale})`,
			};
		},
		shouldBeDisplayed() {
			return Boolean(this.playerName);
		},
	},
	mounted() {
		this.render();
	},
	methods: {
		getAnimatedElements() {
			if (!this.$refs.root) {
				return [];
			}
			const elements = [];
			const iterator = document.createNodeIterator(this.$refs.root, NodeFilter.SHOW_ELEMENT);
			let node = iterator.nextNode();

			while (node) {
				const animations = node.getAnimations({ subtree: true });
				if (animations.length > 0) {
					elements.push({
						node,
						animations,
					});
				}
				node = iterator.nextNode();
			}
			return elements;
		},
		render() {
			this.isAnimated = false;
			this.$nextTick(() => {
				window.requestAnimationFrame(() => {
					this.isAnimated = true;
				});
			});
		},
		reset() {
			const elements = this.getAnimatedElements();
			for (const element of elements) {
				for (const animation of element.animations) {
					animation.currentTime = 0;
				}
			}
			this.render();
		},
		play() {
			const elements = this.getAnimatedElements();
			for (const element of elements) {
				element.node.style.setProperty('animation-play-state', 'running');
			}
		},
		pause() {
			const elements = this.getAnimatedElements();
			for (const element of elements) {
				element.node.style.setProperty('animation-play-state', 'paused');
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

$graphics-duration: 4.5s;
$easing-default: cubic-bezier(0.52, 0, 0, 1);
$easing-team: cubic-bezier(0.34, 0, 0.65, 1);
$top-bar-animation-start: 200ms;
$top-bar-animation-duration: 0.5s;
$main-container-animation-start: 0s;
$main-container-animation-duration: 1000ms;
$main-container-underline-animation-start: 200ms;
$main-container-underline-animation-duration: 900ms;
$player-info-animation-start: 400ms;
$player-info-animation-duration: 900ms;
$team-blade-a-animation-start: 280ms;
$team-blade-a-animation-duration: 270ms;
$team-blade-b-animation-start: 280s;
$team-blade-b-animation-duration: 270s;
$team-logo-animation-start: 470ms;
$team-logo-animation-duration: 750ms;
$center-line-background: var(--color-black);
$player-image-animation-start: 4700ms;
$player-image-animation-duration: 800ms;

.animation-wrapper {
	display: none;
	flex-direction: column;
	overflow: hidden;
	transform-origin: bottom left;
	z-index: 2;
	pointer-events: none;

	.center-line {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		background: $center-line-background;
		padding-right: 16px;
	}

	.center-underline {
		position: relative;
		height: 1px;
		background: var(--color-hype-yellow);

		&.clone {
			position: absolute;
			top: 0;
			left: 0;
		}
	}

	.player-section-image {
		line-height: 0;

		.player-section-image-content {
			display: flex;
			align-items: center;
			opacity: 0;

			&.clone {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 1;
			}
		}

		.player-image {
			width: 72px;
			height: 72px;
			margin-right: 12px;
			object-fit: cover;
		}
	}

	.player-section-info {
		flex: 1 1 auto;
		position: relative;
		overflow: hidden;
		font-size: 32px;

		.player-section-info-content {
			opacity: 0;
			display: flex;
			align-items: center;
			color: var(--color-soft-white);

			&.clone {
				position: absolute;
				top: 0;
				left: 0;
			}
		}
		.player-number {
			font-weight: 300;
			margin: 12px;
		}
		.player-name {
			font-weight: 500;
		}
	}
	.team-section {
		flex: 1 1 0;
		position: relative;
		width: 200px;
		height: 100%;
		margin-left: 24px;
		transform: skewX(-24deg);
		overflow: hidden;

		.blade-container {
			position: absolute;
			top: 0;
			right: 0;
			width: 64px;
			height: 100%;

			.blade {
				position: absolute;
				height: 100%;
				background: green;

				&-a {
					width: 100%;
					background: #37393d;
					top: -100%;
					right: 0;
				}

				&-b {
					width: 20px;
					background: #56595c;
					top: 0;
					right: -100%;
				}
			}
		}

		.team-logo {
			position: relative;
			width: 100%;
			height: 100%;
			padding: 4px 0;
			clip-path: polygon(35% 0, 100% 0%, 100% 100%, 40% 100%);
			display: flex;
			align-items: center;
			justify-content: center;

			img {
				max-width: 100%;
				height: 100%;
				object-fit: scale-down;
			}

			.team-logo-content {
				display: grid;
				place-content: center;
				transform: skew(24deg);
				height: 100%;
				opacity: 0;

				img {
					width: 64px;
					height: 64px;
				}

				&.clone {
					position: absolute;
					opacity: 1;
					top: 0;
					left: -100%;
					width: 100%;

					img {
						margin-left: 16px;
					}
				}
			}
		}
	}

	.top-bar {
		position: relative;
		overflow: hidden;

		.top-bar-content {
			display: inline-flex;
			padding: 6px 16px;
			background: var(--color-hype-yellow);
			color: var(--color-black);
			font-size: 24px;
			opacity: 0;
			font-weight: 600;

			&.clone {
				position: absolute;
				bottom: -100%;
				left: 0;
				opacity: 1;
			}
		}
	}
}

@keyframes slideLeftIntro {
	0.0% {
		left: -100%;
	}
	100% {
		left: 0;
	}
}
@keyframes slideLeftOutro {
	0.0% {
		left: 0;
	}
	100% {
		left: 100%;
	}
}
@keyframes slideFadeTopIntro {
	0.0% {
		top: -100%;
		opacity: 0;
	}
	100% {
		top: 0;
		opacity: 1;
	}
}
@keyframes slideFadeTopOutro {
	0.0% {
		top: 0;
	}
	100% {
		top: 100%;
	}
}
@keyframes slideTopIntro {
	0.0% {
		top: -100%;
		opacity: 1;
	}
	100% {
		top: 0;
	}
}
@keyframes slideTopOutro {
	0.0% {
		top: 0;
	}
	100% {
		top: 100%;
	}
}
@keyframes slideBottomIntro {
	0.0% {
		bottom: -100%;
		opacity: 1;
	}
	100% {
		bottom: 0;
	}
}
@keyframes slideBottomOutro {
	0.0% {
		bottom: 0;
	}
	100% {
		bottom: -100%;
	}
}
@keyframes secondBladeAnimationIntro {
	0.0% {
		right: -100%;
	}
	100% {
		right: 10px;
	}
}
@keyframes secondBladeAnimationOutro {
	0.0% {
		right: 10px;
	}
	100% {
		right: 100%;
	}
}
@keyframes teamLogoAnimationIntro {
	0.0% {
		left: -100%;
	}
	100% {
		left: 10px;
	}
}
@keyframes teamLogoAnimationOutro {
	0.0% {
		left: 10px;
	}
	100% {
		left: 100%;
	}
}
@keyframes playerImageAnimation {
	0.0% {
		left: 0;
		opacity: 1;
	}
	100% {
		left: -100%;
		opacity: 0;
	}
}

.animation-wrapper.animated {
	display: flex;
}

.center-line {
	animation: slideLeftIntro $main-container-animation-duration $easing-default $main-container-animation-start 1,
		slideLeftOutro $main-container-animation-duration $easing-default
			calc(#{$graphics-duration} - #{$main-container-animation-start} + #{$main-container-animation-duration}) 1;
}
.center-underline {
	animation: slideLeftIntro $main-container-underline-animation-duration $easing-default $main-container-underline-animation-start 1,
		slideLeftOutro $main-container-underline-animation-duration $easing-default
			calc(#{$graphics-duration} - #{$main-container-underline-animation-start} + #{$main-container-underline-animation-duration}) 1;
}

.player-section-info-content.clone {
	animation: slideFadeTopIntro $player-info-animation-duration $easing-default $player-info-animation-start 1,
		slideFadeTopOutro $player-info-animation-duration $easing-default
			calc(#{$graphics-duration} - #{$player-info-animation-start} + #{$player-info-animation-duration}) 1;
}

.top-bar-content.clone {
	animation: slideBottomIntro $top-bar-animation-duration $easing-default $top-bar-animation-start 1,
		slideBottomOutro $top-bar-animation-duration $easing-team
			calc(#{$graphics-duration} - #{$top-bar-animation-start} + #{$top-bar-animation-duration}) 1;
}

.blade-a {
	animation: slideTopIntro $team-blade-a-animation-duration $easing-team $team-blade-a-animation-start 1,
		slideTopOutro $team-blade-a-animation-duration $easing-team
			calc(#{$graphics-duration} - #{$team-blade-a-animation-start} + #{$team-blade-a-animation-duration}) 1;
}
.blade-b {
	animation: slideTopIntro $team-blade-b-animation-duration $easing-team $team-blade-b-animation-start 1,
		slideTopOutro $team-blade-b-animation-duration $easing-team
			calc(#{$graphics-duration} - #{$team-blade-b-animation-start} + #{$team-blade-b-animation-duration}) 1;
}

.team-logo-content.clone {
	animation: teamLogoAnimationIntro $team-logo-animation-duration $easing-team $team-logo-animation-start 1,
		teamLogoAnimationOutro $team-logo-animation-duration $easing-team
			calc(#{$graphics-duration} - #{$team-logo-animation-start} + #{$team-logo-animation-duration}) 1;
}

.player-section-image-content.clone {
	animation: playerImageAnimation $player-image-animation-duration $easing-default $player-image-animation-start 1;
}

.center-line,
.center-underline,
.player-section-info-content.clone,
.top-bar-content.clone,
.blade-a,
.blade-b,
.team-logo-content.clone,
.player-section-image-content.clone {
	animation-fill-mode: forwards;
	animation-direction: normal, normal;
}
</style>
